import { useEffect, useState, useCallback } from 'react';
import { apiRequest } from '../Api';

/**
 * Custom hook for fetching educational learning modules
 * Supports filtering by diagnosis keyword and pagination
 *
 * @param {Object} options - Configuration options
 * @param {string} options.diagnosis - Filter modules by diagnosis keyword
 * @param {number} options.limit - Number of modules to fetch
 * @returns {Object} { modules, isLoading, error, refetch }
 */
export const useLearningModules = (options = {}) => {
  const [modules, setModules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchModules = useCallback(async () => {
    // Default fallback modules (used if API endpoint doesn't exist)
    const defaultModules = [
      {
        id: 'pneumonia',
        title: 'Understanding Pneumonia',
        content:
          'Pneumonia is an infection that inflames the air sacs in one or both lungs. It can be caused by bacteria, viruses, or fungi. Treatment typically involves antibiotics, rest, and fluids.',
        category: 'Respiratory',
      },
      {
        id: 'asthma',
        title: 'Managing Asthma',
        content:
          'Asthma is a chronic condition that affects the airways. This module covers the basics of asthma management, including common triggers, medications, and lifestyle adjustments.',
        category: 'Respiratory',
      },
      {
        id: 'diabetes',
        title: 'Diabetes Control and Management',
        content:
          'Diabetes is a chronic disease that impacts the way your body regulates blood sugar. Learn about nutrition, exercise, and medication management in this module.',
        category: 'Endocrine',
      },
      {
        id: 'cardiology',
        title: 'Cardiology Insights',
        content:
          'Cardiology is the study of heart disorders. This module offers insights into common heart conditions, diagnostic techniques, and treatment options.',
        category: 'Cardiovascular',
      },
    ];
    try {
      setIsLoading(true);
      setError(null);

      // Try to fetch from API endpoint
      try {
        const response = await apiRequest('/api/LearningModule');
        let data = response.data || response || [];

        // Filter by diagnosis if provided
        if (options.diagnosis) {
          const diagnosisLower = options.diagnosis.toLowerCase();
          data = data.filter((module) =>
            module.id.toLowerCase().includes(diagnosisLower) ||
            module.title.toLowerCase().includes(diagnosisLower) ||
            module.category?.toLowerCase().includes(diagnosisLower)
          );
        }

        // Apply limit if specified
        if (options.limit) {
          data = data.slice(0, options.limit);
        }

        setModules(data);
      } catch (apiError) {
        // API endpoint doesn't exist or failed, use default modules
        console.log('Using default learning modules (API not available)');
        let data = [...defaultModules];

        // Filter by diagnosis if provided
        if (options.diagnosis) {
          const diagnosisLower = options.diagnosis.toLowerCase();
          data = data.filter((module) =>
            module.id.toLowerCase().includes(diagnosisLower) ||
            module.title.toLowerCase().includes(diagnosisLower)
          );
        }

        // Apply limit if specified
        if (options.limit) {
          data = data.slice(0, options.limit);
        }

        setModules(data);
      }
    } catch (err) {
      setError(err);
      // Still set default modules as fallback
      setModules(defaultModules);
    } finally {
      setIsLoading(false);
    }
  }, [options.diagnosis, options.limit]);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  return {
    modules,
    isLoading,
    error,
    refetch: fetchModules,
  };
};

export default useLearningModules;
